import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, tap, withLatestFrom, filter } from 'rxjs';
import { UserActions } from 'src/app/shared/store/user/user.actions';
import { AuthService } from '../services/auth.service';
import { Store } from '@ngrx/store';
import { UserSelector } from 'src/app/shared/store/user/user.feature';
import { Router } from '@angular/router';
import { ToastrService } from 'src/app/shared/services/toastr/toastr-service';
import { extractErrorMessage } from 'src/app/shared/util/extract-error-message';
import { ROUTES } from 'src/app/app.routes.constants';
import { PassportAuth } from '../services/passport-auth';

@Injectable()
export class UserEffects {
  private action$ = inject(Actions);
  private authService = inject(AuthService);
  private passportAuth = inject(PassportAuth);
  private router = inject(Router);
  private store = inject(Store);
  private toastrService = inject(ToastrService);

  authenticate$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(UserActions.authenticate),
        withLatestFrom(this.store.select(UserSelector.selectReturnUrl)),
        map(([, returnUrl]) => {
          localStorage.clear();
          this.authService.initiateAuthenticationFlow().then(() => {
            if (returnUrl) {
              this.router.navigateByUrl(returnUrl);
            }
          });
        }),
      ),
    { dispatch: false },
  );

  goToReturnUrl$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(UserActions.setUser),
        filter(action => action.goToReturnUrl),
        withLatestFrom(this.store.select(UserSelector.selectReturnUrl)),
        map(([, returnUrl]) => this.router.navigateByUrl(returnUrl || '')),
      ),
    { dispatch: false },
  );

  toastOnAuthError$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(UserActions.authError),
        map(action => {
          console.error('Auth error', action);
          return this.toastrService.error(
            extractErrorMessage(action.error) ?? 'Auth error',
          );
        }),
      ),
    { dispatch: false },
  );

  logout$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(UserActions.logOut),
        map(() => {
          this.passportAuth.setLoggedOutState();
          localStorage.clear();
          this.router.navigate([ROUTES.login]);
          // If we logged in using Azure code, log out using auth service.
          if (localStorage.getItem('encodeAzureID')) {
            this.authService.logout().subscribe();
          }
        }),
      ),
    { dispatch: false },
  );
}
